import { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLocalStorage } from '@/hooks/useLocalStorage'

// ─── Command registry ────────────────────────────────────────────────────────

type CommandCategory = 'navigate' | 'action' | 'settings'

interface Command {
  id: string
  label: string
  icon: string
  category: CommandCategory
  shortcut?: string
  /** For navigate commands */
  to?: string
  /** For action commands */
  action?: () => void
}

const NAV_COMMANDS: Command[] = [
  { id: 'nav-studio',      label: 'Studio',         icon: '◲', category: 'navigate', to: '/studio' },
  { id: 'nav-dashboard',   label: 'Dashboard',      icon: '⊞', category: 'navigate', to: '/' },
  { id: 'nav-live',        label: 'Go Live',        icon: '▶', category: 'navigate', to: '/live',          shortcut: 'G L' },
  { id: 'nav-schedule',    label: 'Schedule',       icon: '📅', category: 'navigate', to: '/schedule' },
  { id: 'nav-chat',        label: 'Chat',           icon: '💬', category: 'navigate', to: '/chat' },
  { id: 'nav-analytics',   label: 'Analytics',      icon: '📊', category: 'navigate', to: '/analytics' },
  { id: 'nav-highlights',  label: 'Highlights',     icon: '✂',  category: 'navigate', to: '/highlights' },
  { id: 'nav-health',      label: 'Stream Health',  icon: '♡', category: 'navigate', to: '/health' },
  { id: 'nav-sponsors',    label: 'Sponsors',       icon: '$',  category: 'navigate', to: '/sponsors' },
  { id: 'nav-alerts',      label: 'Alerts',         icon: '🔔', category: 'navigate', to: '/alerts' },
  { id: 'nav-collab',      label: 'Collab',         icon: '⊕', category: 'navigate', to: '/collab' },
  { id: 'nav-teams',       label: 'Teams',          icon: '⊡', category: 'navigate', to: '/teams' },
  { id: 'nav-coaching',    label: 'Coaching',       icon: '◈', category: 'navigate', to: '/coaching' },
  { id: 'nav-intelligence',label: 'Intelligence',    icon: '⬡', category: 'navigate', to: '/intelligence' },
  { id: 'nav-points',      label: 'Channel Points', icon: '★', category: 'navigate', to: '/points' },
  { id: 'nav-polls',       label: 'Polls',          icon: '◉', category: 'navigate', to: '/polls' },
  { id: 'nav-clips',       label: 'Clips',          icon: '◈', category: 'navigate', to: '/clips' },
  { id: 'nav-vods',        label: 'VODs',           icon: '▣', category: 'navigate', to: '/vods' },
  { id: 'nav-marketplace', label: 'Marketplace',    icon: '◆', category: 'navigate', to: '/marketplace' },
  { id: 'nav-widgets',     label: 'Widgets',        icon: '◇', category: 'navigate', to: '/widgets' },
  { id: 'nav-themes',      label: 'Themes',         icon: '◎', category: 'navigate', to: '/themes' },
  { id: 'nav-settings',    label: 'Settings',       icon: '⚙', category: 'navigate', to: '/settings' },
  { id: 'nav-notifications', label: 'Notifications', icon: '🔔', category: 'navigate', to: '/notifications' },
]

const STATIC_ACTION_COMMANDS: Omit<Command, 'action'>[] = [
  { id: 'action-golive',      label: 'Go Live',          icon: '🔴', category: 'action', shortcut: 'G L' },
  { id: 'action-stostream',   label: 'Stop Stream',      icon: '⏹', category: 'action' },
  { id: 'action-muteall',     label: 'Toggle Mute All',  icon: '🔇', category: 'action', shortcut: 'M' },
  { id: 'action-savereplay',  label: 'Save Replay',      icon: '💾', category: 'action' },
  { id: 'action-recording',   label: 'Start Recording',  icon: '⏺', category: 'action' },
]

const SETTINGS_COMMANDS: Command[] = [
  { id: 'settings-general',label: 'Open Settings',   icon: '⚙', category: 'settings', to: '/settings' },
  { id: 'settings-audio',  label: 'Audio Settings',  icon: '🎙', category: 'settings', to: '/settings?tab=audio' },
  { id: 'settings-stream', label: 'Stream Settings', icon: '📡', category: 'settings', to: '/settings?tab=stream' },
]

const CATEGORY_LABELS: Record<CommandCategory, string> = {
  navigate: 'Navigation',
  action:   'Quick Actions',
  settings: 'Settings',
}

// ─── Fuzzy-ish match ──────────────────────────────────────────────────────────

function matches(label: string, query: string): boolean {
  return label.toLowerCase().includes(query.toLowerCase())
}

// ─── Component ────────────────────────────────────────────────────────────────

const RECENTS_KEY = 'kast:cmd-palette:recents'
const MAX_RECENTS = 5

export function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [recentIds, setRecentIds] = useLocalStorage<string[]>(RECENTS_KEY, [])

  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  // ── Build full command list (with navigate-bound actions) ──────────────────
  const allCommands: Command[] = [
    ...NAV_COMMANDS,
    ...STATIC_ACTION_COMMANDS.map((c) => ({
      ...c,
      action: () => {
        // Placeholder: real implementations would dispatch to a global stream store
        // For nav-able ones, navigate; for pure actions, just close
        if (c.id === 'action-golive') navigate('/live')
      },
    })),
    ...SETTINGS_COMMANDS,
  ]

  // ── Filter ────────────────────────────────────────────────────────────────
  const filtered = query.trim()
    ? allCommands.filter((c) => matches(c.label, query))
    : allCommands

  // Group into sections, recents always first
  const recentCommands = recentIds
    .map((id) => allCommands.find((c) => c.id === id))
    .filter((c): c is Command => c !== undefined)

  interface Section {
    title: string
    commands: Command[]
  }

  const sections: Section[] = query.trim()
    ? Object.entries(
        filtered.reduce<Partial<Record<CommandCategory, Command[]>>>((acc, cmd) => {
          const key = cmd.category
          if (!acc[key]) acc[key] = []
          acc[key]!.push(cmd)
          return acc
        }, {}),
      ).map(([cat, cmds]) => ({ title: CATEGORY_LABELS[cat as CommandCategory], commands: cmds! }))
    : [
        ...(recentCommands.length > 0 ? [{ title: 'Recent', commands: recentCommands }] : []),
        { title: 'Navigation',    commands: NAV_COMMANDS },
        { title: 'Quick Actions', commands: allCommands.filter((c) => c.category === 'action') },
        { title: 'Settings',      commands: SETTINGS_COMMANDS },
      ]

  // Flat list for keyboard nav
  const flatCommands = sections.flatMap((s) => s.commands)

  // ── Open/close logic ──────────────────────────────────────────────────────
  const closePalette = useCallback(() => {
    setOpen(false)
    setQuery('')
    setActiveIndex(0)
  }, [])

  // Global Cmd+K / Ctrl+K listener
  useEffect(() => {
    function handleGlobalKey(e: KeyboardEvent) {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((prev) => {
          if (prev) {
            setQuery('')
            setActiveIndex(0)
            return false
          }
          setQuery('')
          setActiveIndex(0)
          return true
        })
      }
    }
    document.addEventListener('keydown', handleGlobalKey)
    return () => document.removeEventListener('keydown', handleGlobalKey)
  }, [])

  // Auto-focus input when opened
  useEffect(() => {
    if (open) {
      // rAF ensures the DOM is painted before we focus
      const id = requestAnimationFrame(() => inputRef.current?.focus())
      return () => cancelAnimationFrame(id)
    }
  }, [open])

  // Reset active index when query or sections change
  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return
    const active = listRef.current.querySelector<HTMLElement>('[data-active="true"]')
    active?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  // ── Execute a command ─────────────────────────────────────────────────────
  const execute = useCallback(
    (cmd: Command) => {
      // Record in recents
      setRecentIds((prev) => {
        const next = [cmd.id, ...prev.filter((id) => id !== cmd.id)].slice(0, MAX_RECENTS)
        return next
      })

      closePalette()

      if (cmd.to) {
        navigate(cmd.to)
      } else if (cmd.action) {
        cmd.action()
      }
    },
    [closePalette, navigate, setRecentIds],
  )

  // ── Keyboard nav inside palette ───────────────────────────────────────────
  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => (i + 1) % Math.max(flatCommands.length, 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => (i - 1 + Math.max(flatCommands.length, 1)) % Math.max(flatCommands.length, 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const cmd = flatCommands[activeIndex]
      if (cmd) execute(cmd)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      closePalette()
    }
  }

  // ── Detect Mac for hint badge ─────────────────────────────────────────────
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
  const shortcutHint = isMac ? '⌘K' : 'Ctrl+K'

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Discoverable hint badge — rendered as a sibling so Layout can place it */}
      <div
        id="cmd-palette-hint"
        style={{ display: 'none' }}
        data-shortcut={shortcutHint}
        aria-hidden="true"
      />

      {/* Overlay */}
      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] px-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closePalette()
          }}
          role="presentation"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Command palette"
            className="relative w-full rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            style={{
              maxWidth: '640px',
              maxHeight: '70vh',
              backgroundColor: 'var(--bg-card)',
              border: '1px solid #1e1e3a',
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {/* Search input row */}
            <div
              className="flex items-center gap-3 px-4 py-3.5 shrink-0"
              style={{ borderBottom: '1px solid #1e1e3a' }}
            >
              {/* Search icon */}
              <span className="text-gray-500 text-base shrink-0" aria-hidden="true">
                ⌕
              </span>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a command or search..."
                className="flex-1 bg-transparent text-white placeholder-gray-500 text-sm outline-none"
                aria-label="Search commands"
                aria-autocomplete="list"
                aria-controls="cmd-palette-list"
                aria-activedescendant={
                  flatCommands[activeIndex] ? `cmd-${flatCommands[activeIndex].id}` : undefined
                }
                autoComplete="off"
                spellCheck={false}
              />
              {/* ESC hint */}
              <kbd
                className="hidden sm:flex items-center gap-1 text-gray-500 text-xs rounded px-1.5 py-0.5 shrink-0"
                style={{ backgroundColor: 'var(--bg-card)', border: '1px solid #2a2a4a' }}
                aria-hidden="true"
              >
                ESC
              </kbd>
            </div>

            {/* Results list */}
            <ul
              id="cmd-palette-list"
              ref={listRef}
              role="listbox"
              aria-label="Commands"
              className="overflow-y-auto py-2"
              style={{ maxHeight: 'calc(70vh - 58px)' }}
            >
              {flatCommands.length === 0 ? (
                <li className="px-4 py-8 text-center text-gray-500 text-sm" role="option" aria-selected={false}>
                  No commands found for &ldquo;{query}&rdquo;
                </li>
              ) : (
                sections.map((section) => {
                  // Compute flat offset for this section for activeIndex alignment
                  const sectionOffset = flatCommands.indexOf(section.commands[0])

                  return (
                    <li key={section.title} role="presentation">
                      {/* Section header */}
                      <p
                        className="px-4 pt-3 pb-1.5 text-xs font-semibold uppercase tracking-wider"
                        style={{ color: 'var(--text-muted)' }}
                        aria-hidden="true"
                      >
                        {section.title}
                      </p>
                      <ul role="group" aria-label={section.title}>
                        {section.commands.map((cmd, localIdx) => {
                          const globalIdx = sectionOffset + localIdx
                          const isActive = globalIdx === activeIndex

                          return (
                            <li
                              key={cmd.id}
                              id={`cmd-${cmd.id}`}
                              role="option"
                              aria-selected={isActive}
                              data-active={isActive}
                              onMouseEnter={() => setActiveIndex(globalIdx)}
                              onClick={() => execute(cmd)}
                              className="flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors"
                              style={{
                                backgroundColor: isActive ? '#7c3aed22' : 'transparent',
                              }}
                            >
                              {/* Icon */}
                              <span
                                className="text-base leading-none w-5 text-center shrink-0"
                                style={{ color: isActive ? '#a78bfa' : 'var(--text-secondary)' }}
                                aria-hidden="true"
                              >
                                {cmd.icon}
                              </span>

                              {/* Label */}
                              <span
                                className="flex-1 text-sm font-medium"
                                style={{ color: isActive ? '#fff' : '#d1d5db' }}
                              >
                                {cmd.label}
                              </span>

                              {/* Keyboard shortcut hint */}
                              {cmd.shortcut && (
                                <span
                                  className="flex items-center gap-1 text-xs shrink-0"
                                  style={{ color: 'var(--text-muted)' }}
                                  aria-hidden="true"
                                >
                                  {cmd.shortcut.split(' ').map((key, i) => (
                                    <kbd
                                      key={i}
                                      className="px-1.5 py-0.5 rounded text-xs"
                                      style={{
                                        backgroundColor: 'var(--bg-card)',
                                        border: '1px solid #2a2a4a',
                                        color: isActive ? '#a78bfa' : 'var(--text-muted)',
                                      }}
                                    >
                                      {key}
                                    </kbd>
                                  ))}
                                </span>
                              )}

                              {/* Category badge for actions (subtle) */}
                              {cmd.category === 'action' && !cmd.shortcut && (
                                <span
                                  className="text-xs rounded-full px-2 py-0.5 shrink-0"
                                  style={{
                                    backgroundColor: 'var(--bg-card)',
                                    color: 'var(--text-muted)',
                                    border: '1px solid #2a2a4a',
                                  }}
                                  aria-hidden="true"
                                >
                                  action
                                </span>
                              )}
                            </li>
                          )
                        })}
                      </ul>
                    </li>
                  )
                })
              )}
            </ul>

            {/* Footer hint bar */}
            <div
              className="flex items-center gap-4 px-4 py-2.5 shrink-0 text-xs"
              style={{
                borderTop: '1px solid #1e1e3a',
                color: '#4b5563',
              }}
              aria-hidden="true"
            >
              <span className="flex items-center gap-1.5">
                <kbd
                  className="px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: 'var(--bg-card)', border: '1px solid #2a2a4a', color: 'var(--text-muted)' }}
                >
                  ↑↓
                </kbd>
                navigate
              </span>
              <span className="flex items-center gap-1.5">
                <kbd
                  className="px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: 'var(--bg-card)', border: '1px solid #2a2a4a', color: 'var(--text-muted)' }}
                >
                  ↵
                </kbd>
                select
              </span>
              <span className="flex items-center gap-1.5">
                <kbd
                  className="px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: 'var(--bg-card)', border: '1px solid #2a2a4a', color: 'var(--text-muted)' }}
                >
                  ESC
                </kbd>
                close
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// Re-export the hint badge shortcut string for Layout to use
export function CmdKBadge() {
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
  return (
    <div
      className="flex items-center justify-center gap-1 text-xs rounded-md px-2 py-1 cursor-default select-none"
      style={{
        backgroundColor: '#0d0d20',
        border: '1px solid #1e1e3a',
        color: '#4b5563',
      }}
      title="Open command palette"
      aria-hidden="true"
    >
      <span>{isMac ? '⌘' : 'Ctrl'}</span>
      <span>K</span>
    </div>
  )
}
